/**
 * Client ของ Packing Unit (`/v1/packing-units`, D-105) — ตาม route ที่ router
 * จริงของเซิร์ฟเวอร์ serve (`modules/sales/router.py`), **ไม่ใช่ตาม
 * `docs/API_CONTRACT.md` ทั้งหมด** — ดูหมายเหตุยาวใน `types.ts` เรื่อง
 * `POST /v1/packing-units/{id}/lots` ที่เอกสารเอ่ยถึงแต่ backend ไม่ได้ลงทะเบียน
 * ไฟล์นี้จึง implement เฉพาะห้าเส้นที่ router จริง serve: `GET`/`POST` บน
 * `/v1/packing-units`, `GET` หนึ่งใบ, `confirm` และ `unpack`
 *
 * **ฝาแฝดของ `lib/sales/client.ts` ไม่ใช่ผู้ใช้ของมัน** — เหตุผลเดียวกับที่ไฟล์
 * นั้นไม่ import จาก `lib/procurement/client.ts`: `lib/sales/**` อยู่ใน
 * forbidden_scope ของ Work Order นี้ และแต่ละโดเมนมีชุดความล้มเหลว/โครงเป็นของ
 * ตัวเองโดยตั้งใจ
 *
 * หลักการเดียวกับ client อื่นทุกตัวในโปรเจกต์นี้:
 * - ฟังก์ชันในไฟล์นี้ **ไม่ throw** ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้
 * - payload ที่ผิดรูปคือ `invalid-response` ไม่ใช่ "ไม่มีกล่อง"
 * - **ไม่มี fallback ที่แต่งข้อมูลขึ้นมาเอง** และ **ไม่เขียนอะไรลง storage เลย**
 * - ทุกคำขอเดินทางผ่าน `apiFetch` ตัวเดียวกับ client อื่นทุกตัว — header
 *   `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว **ไม่ใช่ที่นี่**
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 * (`references/authorization-rbac.md` ข้อ 5)
 */

import type {
  CreatePackingUnitBody,
  PackingUnit,
  PackingUnitListPage,
  PackingUnitLot,
  PackingUnitSummary,
} from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

export const PACKING_UNITS_PATH = '/v1/packing-units';

/** `packing_unit_id` เป็น UUID surrogate (D-039) — encode ไว้กันค่าที่มาจาก
 * payload ของเซิร์ฟเวอร์เปลี่ยนเส้นทางทั้งเส้นถ้าวันหนึ่งมันไม่ใช่สิ่งที่คิดไว้ */
export function packingUnitPath(packingUnitId: string): string {
  return `${PACKING_UNITS_PATH}/${encodeURIComponent(packingUnitId)}`;
}

/** สอง transition ที่หน้าจอนี้ขับได้ — `load`/`dispatch`/`reverse` เป็นของ
 * Delivery (`WO-P2-M9-007`, แยก Work Order) ไม่ใช่ของที่นี่ */
export type PackingUnitTransition = 'confirm' | 'unpack';

export function packingUnitTransitionPath(
  packingUnitId: string,
  transition: PackingUnitTransition,
): string {
  return `${packingUnitPath(packingUnitId)}/${transition}`;
}

/**
 * พารามิเตอร์ของ `GET /v1/packing-units` ที่ router รับจริง — ไม่มีตัวที่สี่
 *
 * ทั้งสามแคบลงได้เท่านั้น ไม่เคยกว้างขึ้น: เซิร์ฟเวอร์กรองตาม `plant_scope` ของ
 * ผู้เรียกก่อนเสมอ และ `plant_code` นอกขอบเขตตอบ `403` ไม่ใช่หน้าว่าง
 */
export interface PackingUnitListQuery {
  readonly plant_code?: string;
  readonly so_number?: string;
  readonly status?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

export function buildPackingUnitListQuery(query: PackingUnitListQuery = {}): string {
  const params = new URLSearchParams();
  const plantCode = query.plant_code?.trim() ?? '';
  if (plantCode !== '') {
    params.append('plant_code', plantCode);
  }
  const soNumber = query.so_number?.trim() ?? '';
  if (soNumber !== '') {
    params.append('so_number', soNumber);
  }
  const status = query.status?.trim() ?? '';
  if (status !== '') {
    params.append('status', status);
  }
  if (query.limit !== undefined) {
    params.append('limit', String(query.limit));
  }
  const cursor = query.cursor?.trim() ?? '';
  if (cursor !== '') {
    params.append('cursor', cursor);
  }
  const serialized = params.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  /** `details.rule` — วิธีที่ contract ใช้แยกกรณีทางธุรกิจโดยไม่เพิ่ม top-level
   * error code ใหม่ */
  readonly rule: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = {
  error_code: null,
  message: null,
  rule: null,
  details: {},
};

/**
 * ความล้มเหลวทุกแบบที่ router นี้ตอบจริง แยกเป็นสถานะของตัวเอง — โครงเดียวกับ
 * `SalesOrderFailure` ของ `lib/sales/client.ts`
 */
export type PackingUnitFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'not-found'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'conflict'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'rule-violation'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type PackingUnitListResult =
  | { readonly status: 'ready'; readonly page: PackingUnitListPage }
  | PackingUnitFailure;

/** คำตอบของเส้นที่คืนกล่อง **พร้อม lot** — `GET /{id}`, `POST`, `confirm`, `unpack` */
export type PackingUnitDetailResult =
  | { readonly status: 'ready'; readonly packingUnit: PackingUnit }
  | PackingUnitFailure;

export interface PackingUnitRequestOptions {
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  readonly fetchImpl?: typeof fetch;
}

/* -------------------------------------------------------------- parsing -- */

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function requiredInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) ? value : null;
}

/** `undefined` แปลว่า "ผิดรูป" — คีย์ที่หายไปทั้งคีย์หรือมาเป็นชนิดผิด ถือว่าผิดรูป
 * เท่ากัน ส่วน `null` (ตามที่ `Optional[...] = None` ของ Pydantic serialize ออกมา)
 * เป็นค่าที่ถูกต้อง */
function nullableString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

function parseCursor(raw: unknown): string | null | undefined {
  const cursor = nullableString(raw);
  if (cursor === undefined) {
    return undefined;
  }
  return cursor === null || cursor.trim() === '' ? null : cursor;
}

/** หนึ่ง lot ในกล่อง — `PackingUnitLotResponse` มีแค่สองฟิลด์ ทั้งคู่ required */
export function parsePackingUnitLot(raw: unknown): PackingUnitLot | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const lotId = requiredString(record.lot_id);
  const packedQty = requiredString(record.packed_qty);
  if (lotId === null || packedQty === null) {
    return null;
  }
  return { lot_id: lotId, packed_qty: packedQty };
}

/**
 * กล่องหนึ่งใบ **ไม่รวม `lots`** — ใช้ร่วมกันระหว่างแถวในรายการ
 * (`PackingUnitListItemResponse`) และเป็นฐานของกล่องเต็มใบ
 *
 * ทุกฟิลด์ตรงตาม `PackingUnitResponse`/`PackingUnitListItemResponse` ใน
 * `schemas.py` — เพราะไฟล์นี้มีซอร์สของเซิร์ฟเวอร์ให้อ่านจริง (ต่างจาก
 * `lib/sales/client.ts` ที่ยอมให้ฟิลด์ประกอบ nullable เพราะ contract เอกสาร
 * ไม่ได้เขียนตัวอย่าง response ไว้ทีละฟิลด์) เก้าฟิลด์แรกและ `packed_qty`/
 * `uom_code`/`status`/`created_by_employee_id`/`created_at`/`updated_at` จึง
 * **required ทั้งหมด** และมีแค่สี่ฟิลด์ pack/unpack record ที่ nullable
 */
export function parsePackingUnitSummary(raw: unknown): PackingUnitSummary | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const packingUnitId = requiredString(record.packing_unit_id);
  const packageNo = requiredString(record.package_no);
  const plantId = requiredString(record.plant_id);
  const plantCode = requiredString(record.plant_code);
  const soId = requiredString(record.so_id);
  const soNumber = requiredString(record.so_number);
  const lineNo = requiredInteger(record.line_no);
  const productId = requiredString(record.product_id);
  const productCode = requiredString(record.product_code);
  const packingStandardId = requiredString(record.packing_standard_id);
  const storageLocationId = requiredString(record.storage_location_id);
  const storageLocationCode = requiredString(record.storage_location_code);
  const packedQty = requiredString(record.packed_qty);
  const uomCode = requiredString(record.uom_code);
  const status = requiredString(record.status);
  const createdByEmployeeId = requiredString(record.created_by_employee_id);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);

  if (
    packingUnitId === null ||
    packageNo === null ||
    plantId === null ||
    plantCode === null ||
    soId === null ||
    soNumber === null ||
    lineNo === null ||
    productId === null ||
    productCode === null ||
    packingStandardId === null ||
    storageLocationId === null ||
    storageLocationCode === null ||
    packedQty === null ||
    uomCode === null ||
    status === null ||
    createdByEmployeeId === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  const packedByEmployeeId = nullableString(record.packed_by_employee_id);
  const packedAt = nullableString(record.packed_at);
  const unpackedByEmployeeId = nullableString(record.unpacked_by_employee_id);
  const unpackedAt = nullableString(record.unpacked_at);

  if (
    packedByEmployeeId === undefined ||
    packedAt === undefined ||
    unpackedByEmployeeId === undefined ||
    unpackedAt === undefined
  ) {
    return null;
  }

  return {
    packing_unit_id: packingUnitId,
    package_no: packageNo,
    plant_id: plantId,
    plant_code: plantCode,
    so_id: soId,
    so_number: soNumber,
    line_no: lineNo,
    product_id: productId,
    product_code: productCode,
    packing_standard_id: packingStandardId,
    storage_location_id: storageLocationId,
    storage_location_code: storageLocationCode,
    packed_qty: packedQty,
    uom_code: uomCode,
    status,
    created_by_employee_id: createdByEmployeeId,
    packed_by_employee_id: packedByEmployeeId,
    packed_at: packedAt,
    unpacked_by_employee_id: unpackedByEmployeeId,
    unpacked_at: unpackedAt,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

/** กล่องเต็มใบ **พร้อม lot ครบ** — `lots` แถวเดียวที่อ่านไม่ออกทำให้ทั้งกล่องผิดรูป
 * ไม่ใช่ถูกตัดทิ้งเงียบ ๆ: lot ที่หายไปคือของที่ delivery tag จะไม่พิมพ์ (flow 7.5) */
export function parsePackingUnit(raw: unknown): PackingUnit | null {
  const summary = parsePackingUnitSummary(raw);
  if (summary === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.lots)) {
    return null;
  }

  const lots: PackingUnitLot[] = [];
  for (const entry of record.lots) {
    const lot = parsePackingUnitLot(entry);
    if (lot === null) {
      return null;
    }
    lots.push(lot);
  }

  return { ...summary, lots };
}

export function parsePackingUnitListPage(raw: unknown): PackingUnitListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }

  const items: PackingUnitSummary[] = [];
  for (const entry of record.items) {
    const parsed = parsePackingUnitSummary(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = parseCursor(record.next_cursor);
  return cursor === undefined ? null : { items, next_cursor: cursor };
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ENVELOPE;
  }
  const record = raw as Record<string, unknown>;
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};

  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    rule: requiredString(details.rule),
    details,
  };
}

/* ---------------------------------------------------------- serializing -- */

/**
 * แปลง body ของ `POST /v1/packing-units` ให้เหลือเฉพาะคีย์ที่ contract รับ —
 * เขียนคีย์ทีละตัวแทนการ spread เพื่อไม่ให้ฟิลด์ที่เซิร์ฟเวอร์เป็นเจ้าของ
 * (`package_no`, `status`, `packing_standard_id`, ...) หลุดไปกับ state ของฟอร์ม
 */
export function serializeCreatePackingUnitBody(
  body: CreatePackingUnitBody,
): Record<string, unknown> {
  return {
    plant_code: body.plant_code,
    so_number: body.so_number,
    line_no: body.line_no,
    product_code: body.product_code,
    storage_location_code: body.storage_location_code,
    packed_qty: body.packed_qty,
    lots: body.lots.map((lot) => ({
      lot_id: lot.lot_id,
      packed_qty: lot.packed_qty,
    })),
  };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    return EMPTY_ENVELOPE;
  }
}

type PayloadResult =
  | { readonly status: 'payload'; readonly payload: unknown }
  | PackingUnitFailure;

async function requestPayload(
  path: string,
  init: RequestInit,
  options: PackingUnitRequestOptions,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

function toDetailResult(result: PayloadResult): PackingUnitDetailResult {
  if (result.status !== 'payload') {
    return result;
  }
  const packingUnit = parsePackingUnit(result.payload);
  return packingUnit === null
    ? { status: 'invalid-response' }
    : { status: 'ready', packingUnit };
}

/**
 * `GET /v1/packing-units` — กรองตามขอบเขตโรงงานของผู้เรียกเสมอ, ไม่มี `lots`
 *
 * **ไม่เรียงลำดับซ้ำเอง** — เรียงตาม `package_no` ที่เซิร์ฟเวอร์ส่งมา
 */
export async function fetchPackingUnits(
  query: PackingUnitListQuery = {},
  options: PackingUnitRequestOptions = {},
): Promise<PackingUnitListResult> {
  const result = await requestPayload(
    `${PACKING_UNITS_PATH}${buildPackingUnitListQuery(query)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parsePackingUnitListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/** `GET /v1/packing-units/{id}` — กล่องเดียว **พร้อม lot ครบ** เพื่อพิมพ์ delivery
 * tag (flow ข้อ 7.5) — กล่องนอกขอบเขตโรงงานตอบ `404` เหมือนไม่มีอยู่จริง */
export async function fetchPackingUnit(
  packingUnitId: string,
  options: PackingUnitRequestOptions = {},
): Promise<PackingUnitDetailResult> {
  return toDetailResult(
    await requestPayload(
      packingUnitPath(packingUnitId),
      { method: 'GET', headers: { Accept: 'application/json' } },
      options,
    ),
  );
}

/**
 * `POST /v1/packing-units` — เปิดกล่องที่ `PACKING` **พร้อม lot ทั้งหมดในคำขอ
 * เดียว** ตอบ `201`
 *
 * ไม่มี `Idempotency-Key`: การเปิดกล่องยังไม่เคลื่อนสต๊อกหรือลงบัญชี (ตรงข้ามกับ
 * `POST /v1/deliveries` ซึ่งเป็นของ Delivery Work Order แยก)
 */
export async function createPackingUnit(
  body: CreatePackingUnitBody,
  options: PackingUnitRequestOptions = {},
): Promise<PackingUnitDetailResult> {
  return toDetailResult(
    await requestPayload(
      PACKING_UNITS_PATH,
      {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(serializeCreatePackingUnitBody(body)),
      },
      options,
    ),
  );
}

/**
 * body ของ transition — **ว่างเปล่าเสมอ**
 *
 * `PackingUnitTransitionRequest` มีฟิลด์เดียวคือ `expected_status` (optional)
 * หน้าจอนี้ไม่ส่งมันเลย เหมือน `lib/sales/client.ts`: การส่ง `expected_status`
 * เปิด staleness guard เพิ่ม (`409 PACKING_UNIT_STATUS_CHANGED`) ซึ่งไม่ใช่สิ่งที่
 * Work Order นี้ต้องพิสูจน์ — คีย์ที่ไม่ได้ประกาศไว้ใน `extra="forbid"` จะตอบ
 * `400` จึงส่ง object ว่างเปล่าเท่านั้น ไม่ใช่ omit body
 */
function transitionBody(): string {
  return JSON.stringify({});
}

async function requestTransition(
  packingUnitId: string,
  transition: PackingUnitTransition,
  options: PackingUnitRequestOptions,
): Promise<PackingUnitDetailResult> {
  return toDetailResult(
    await requestPayload(
      packingUnitTransitionPath(packingUnitId, transition),
      {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: transitionBody(),
      },
      options,
    ),
  );
}

/**
 * `POST /v1/packing-units/{id}/confirm` — `PACKING → PACKED` (EDIT)
 *
 * **THE SUM RULE**: กล่องที่ lot รวมกันไม่เท่ากับ `packed_qty` ที่ประกาศไว้ตอบ
 * `422 PACKED_QUANTITY_MUST_MATCH_LOT_TOTAL` พร้อมตัวเลขทั้งสองข้างและผลต่าง —
 * ไม่มี constraint ฐานข้อมูลใดถือกฎนี้ (D-105 ข้อ 1)
 */
export async function confirmPackingUnit(
  packingUnitId: string,
  options: PackingUnitRequestOptions = {},
): Promise<PackingUnitDetailResult> {
  return requestTransition(packingUnitId, 'confirm', options);
}

/**
 * `POST /v1/packing-units/{id}/unpack` — `PACKED → PACKING` (EDIT, **ไม่ใช่**
 * APPROVE — การกลับ Delivery ต่างหากที่เป็น APPROVE)
 *
 * นี่คือเส้นทาง "แก้ไข" กล่องที่ยืนยันไปแล้วเพียงเส้นเดียว (D-105 ข้อ 4) — ไม่มี
 * route ใดแก้จำนวนของกล่องที่ `PACKED` ตรง ๆ กล่องที่ `RETURNED` เปิดไม่ได้ในเฟส
 * นี้ (`422 RETURNED_PACKAGE_CANNOT_BE_UNPACKED`)
 */
export async function unpackPackingUnit(
  packingUnitId: string,
  options: PackingUnitRequestOptions = {},
): Promise<PackingUnitDetailResult> {
  return requestTransition(packingUnitId, 'unpack', options);
}

/** อ่าน `details.rule` จากความล้มเหลวที่มี envelope — คืน `null` เมื่อไม่มี */
export function ruleFromFailure(failure: PackingUnitFailure): string | null {
  return 'error' in failure ? failure.error.rule : null;
}

/** อ่าน `details.current_status` — สถานะที่กล่องนั้นถืออยู่จริงตอน `409`/`422` ที่
 * เกี่ยวกับ transition */
export function currentStatusFromFailure(failure: PackingUnitFailure): string | null {
  if (!('error' in failure)) {
    return null;
  }
  const value = failure.error.details.current_status;
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}
